import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setActiveCatalogOwner } from "../../api/catalogScope";
import type { CatalogSummary } from "../../api/types";
import { useMe } from "../../api/queries";

const STORAGE_KEY = "art-directory-catalog-owner";

interface CatalogContextValue {
  catalogs: CatalogSummary[];
  active: CatalogSummary | null;
  isOwnCatalog: boolean;
  setCatalogOwner: (ownerTelegramId: number) => void;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const client = useQueryClient();
  const catalogs =
    me.data?.catalogs && me.data.catalogs.length > 0
      ? me.data.catalogs
      : me.data
        ? [
            {
              owner_telegram_id: me.data.user.telegram_id,
              display_name: me.data.user.display_name,
              is_own: true,
            },
          ]
        : [];
  const ownId = me.data?.user.telegram_id ?? null;

  const [ownerId, setOwnerId] = useState<number | null>(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });

  useEffect(() => {
    if (!ownId || catalogs.length === 0) return;
    const allowed = new Set(catalogs.map((row) => row.owner_telegram_id));
    const next = ownerId !== null && allowed.has(ownerId) ? ownerId : ownId;
    if (next !== ownerId) setOwnerId(next);
    setActiveCatalogOwner(next === ownId ? null : next);
  }, [catalogs, ownId, ownerId]);

  const setCatalogOwner = useCallback(
    (telegramId: number) => {
      setOwnerId(telegramId);
      localStorage.setItem(STORAGE_KEY, String(telegramId));
      setActiveCatalogOwner(ownId !== null && telegramId === ownId ? null : telegramId);
      void client.invalidateQueries();
    },
    [client, ownId],
  );

  const active = useMemo(
    () => catalogs.find((row) => row.owner_telegram_id === (ownerId ?? ownId)) ?? null,
    [catalogs, ownerId, ownId],
  );

  const value = useMemo(
    () => ({
      catalogs,
      active,
      isOwnCatalog: Boolean(active?.is_own),
      setCatalogOwner,
    }),
    [active, catalogs, setCatalogOwner],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalogScope(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalogScope requires CatalogProvider");
  return ctx;
}
